import { useState } from 'react';
import { adminFacilities, adminUserDelete, adminUserUpdate, adminUsersBulk, facilityOrganization, facilityRosterImport, type ApiError, type RosterRow } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ErrLine, I, pre, sx, useApi, useGuarded, usePage } from './kit';

type Kind = 'Staff' | 'Residents';
/** a name, b email or room, c role or consent, x the facility's own id for them (external_id),
 *  p a staff member's mobile number (the phone column). */
interface Row { a: string; b: string; c: string; flag: string; x?: string; role?: string; p?: string }
interface Added extends Row { id: string | null }

const ROLES = ['Facility Admin', 'Care Staff', 'Nurse', 'CNA'];
/** A roster file's own role words (roster-import.ts ROSTER_STAFF_ROLES) and what we call them. */
const FILE_ROLES: Record<string, string> = { admin: 'Facility Admin', caregiver: 'Care Staff', staff: 'Care Staff' };
/** The server role for a pasted role word (a Facility Admin may give ADMIN, CAREGIVER, STAFF). */
const SERVER_ROLE = (c: string) => (c.toLowerCase() === 'facility admin' ? 'ADMIN' : 'CAREGIVER');

/** Header words a roster export uses, by the field they fill. */
const HEADERS: Record<string, keyof Cols> = {
  name: 'name', 'full name': 'name', resident: 'name', 'resident name': 'name', 'staff name': 'name',
  room: 'room', 'room number': 'room', unit: 'room',
  email: 'email', 'email address': 'email', 'work email': 'email',
  phone: 'phone', 'phone number': 'phone', mobile: 'phone', 'mobile phone': 'phone', 'mobile number': 'phone', cell: 'phone', 'cell phone': 'phone',
  role: 'role', title: 'role',
  consent: 'consent',
  external_id: 'ext', 'external id': 'ext', id: 'ext', mrn: 'ext', 'resident id': 'ext', 'employee id': 'ext',
  entity_type: 'entity', type: 'entity',
};
interface Cols { name?: number; room?: number; email?: number; role?: number; consent?: number; ext?: number; entity?: number; phone?: number }

const cellsOf = (line: string) => line.split(/\t|,|;/).map((c) => c.trim().replace(/^"|"$/g, ''));

/** A cell that looks like a phone number (8 to 15 digits). The server makes it +E.164 and
 *  says so when it is not one; nothing typed here is ever texted until the person confirms
 *  the number with a code. */
const looksLikePhone = (c: string) => /^\+?[\d\s().-]{8,20}$/.test(c) && c.replace(/\D/g, '').length >= 8;

/**
 * One parser for typed rows and a dropped CSV (peopleVals parse, L28605). A header row
 * names the columns (a roster export: entity_type, external_id, name, room, email, phone,
 * role) and is read by those names; without one, the columns are name, room or email, then
 * consent or role, as typed, and a staff cell that looks like a phone number is the phone.
 */
export function parse(text: string, k: Kind): { rows: Row[]; bad: { n: string; why: string; line: string }[] } {
  const rows: Row[] = [];
  const bad: { n: string; why: string; line: string }[] = [];
  let cols: Cols | null = null;
  let first = true;
  String(text).split(/\r?\n/).forEach((line, i) => {
    const s = line.trim();
    if (!s) return;
    const cells = cellsOf(s);
    if (first) {
      first = false;
      const found: Cols = {};
      cells.forEach((c, j) => {
        const f = HEADERS[c.toLowerCase()];
        if (f && found[f] === undefined) found[f] = j;
      });
      if (found.name !== undefined) {
        cols = found;
        return;
      }
      if (/^(name|full name)\b/i.test(cells[0] || '')) return;
    }
    const at = (f: keyof Cols): string => {
      const j = cols?.[f];
      return j === undefined ? '' : cells[j] || '';
    };
    const name = cols ? at('name') : cells[0] || '';
    const entity = cols ? at('entity').toLowerCase() : '';
    if (entity && entity !== (k === 'Staff' ? 'staff' : 'resident')) {
      bad.push({ n: String(i + 1), why: 'a ' + entity + ' row: switch to ' + (k === 'Staff' ? 'Residents' : 'Staff') + ' to add it', line: s.slice(0, 44) });
      return;
    }
    if (!name) {
      bad.push({ n: String(i + 1), why: cols ? 'no name in the name column' : 'no name in the first column', line: s.slice(0, 44) });
      return;
    }
    const x = cols ? at('ext') || undefined : undefined;
    if (k === 'Staff') {
      const email = cols ? at('email') : cells.find((c) => c.indexOf('@') > 0) || '';
      const word = cols ? at('role') : cells.slice(1).find((c) => ROLES.some((r) => r.toLowerCase() === c.toLowerCase()) || FILE_ROLES[c.toLowerCase()]) || '';
      const known = ROLES.find((r) => r.toLowerCase() === word.toLowerCase()) || FILE_ROLES[word.toLowerCase()];
      if (!email || email.indexOf('@') < 1) {
        bad.push({ n: String(i + 1), why: 'no email, so we cannot invite them', line: s.slice(0, 44) });
        return;
      }
      const phone = cols ? at('phone') : cells.slice(1).find(looksLikePhone) || '';
      // No phone: they are added, but no alert can reach their phone until they add one.
      const flag = [known ? '' : 'role defaulted', phone ? '' : 'needs a phone number'].filter(Boolean).join(' · ');
      rows.push({ a: name, b: email, c: known || 'Care Staff', flag, x, ...(phone ? { p: phone } : {}), ...(FILE_ROLES[word.toLowerCase()] ? { role: word.toUpperCase() } : {}) });
    } else {
      const room = cols ? at('room') : cells[1] || '';
      if (!room) {
        bad.push({ n: String(i + 1), why: 'no room number', line: s.slice(0, 44) });
        return;
      }
      const consent = cols ? at('consent') : cells[2] || '';
      rows.push({ a: name, b: room, c: consent || 'consent not set', flag: consent ? '' : 'consent off until set', x });
    }
  });
  return { rows, bad };
}

const chipK = (on: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 13px;border-radius:8px;font-size:16px;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:#2F5C97;color:#fff;font-weight:600;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);');
const btn2 = (k: 'go' | 'off' | 'soft') =>
  'display:inline-flex;align-items:center;height:32px;padding:0 15px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' +
  (k === 'go' ? 'background:#2F5C97;color:#fff;' : k === 'off' ? 'background:rgba(34,39,46,.05);color:#54606C;border:1px solid rgba(34,39,46,.12);pointer-events:none;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);');
const HEAD = 'font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#54606C;font-weight:700;';
const EDIT_IN = (flex: string) =>
  "box-sizing:border-box;" + flex + "height:30px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-family:'Archivo',system-ui,sans-serif;font-size:16px;color:#22272E;";

/**
 * Add people in bulk (design T8891, peopleVals L28597): paste or pick a CSV, see what will
 * be added and what was skipped, then add. Staff become accounts (POST /admin/users/bulk);
 * residents come in through the roster import (POST /admin/facilities/:orgId/roster).
 * The "added" list is what this session added; edit and remove change those accounts.
 */
export function BulkAdd({ onAdded }: { onAdded: () => void }) {
  const { client } = useSession();
  const { lens, facilityId, ask } = usePage();
  const { canWrite, roReason } = useGuarded();
  const fa = lens.lens === 'Facility Admin';
  const [kind, setKind] = useState<Kind>('Staff');
  const [paste, setPaste] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileErr, setFileErr] = useState('');
  const [added, setAdded] = useState<Record<Kind, Added[]>>({ Staff: [], Residents: [] });
  const [edit, setEdit] = useState<{ i: number; a: string; b: string; c: string } | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  // Aithora's team (whose own account sits in Aithora's organization, not a building)
  // picks the building the people go to: a roster never lands in Aithora's own org.
  const buildings = useApi((c, s) => adminFacilities(c, { signal: s }), [], !fa);
  // A Facility Admin's own building, for its BAA status.
  const own = useApi((c, s) => facilityOrganization(c, { params: { id: 'me' }, signal: s }), [], fa);
  const [buildingId, setBuildingId] = useState('');
  const target = fa ? facilityId : buildingId || null;
  const out = parse(paste, kind);
  const committed = added[kind];
  // Residents belong to a building.
  const noBuilding = kind === 'Residents' && !target;
  // HIPAA: no resident data before the building's BAA is signed by both sides and Aithora
  // has recorded it (the server refuses them too, src/aithora/baa-gate.ts). Unknown counts
  // as not signed; the server has the last word either way.
  const baa = fa ? own.data?.organization.baaStatus : (buildings.data?.facilities ?? []).find((f) => f.id === buildingId)?.baa_status;
  const noBaa = kind === 'Residents' && !!target && baa !== 'countersigned';
  // A roster file's rows carry the facility's own ids: they go through the roster import,
  // which updates a person already loaded instead of adding them twice.
  const keyed = out.rows.some((x) => x.x);
  const commitOff = !out.rows.length || busy || noBaa;

  const commit = async () => {
    if (commitOff || !canWrite) return;
    if (noBuilding) {
      setErr({ message: fa ? 'Residents are added from their own building.' : 'Pick the building these residents live in first.' } as ApiError);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      let next: Added[] = [];
      const roster = async (rows: RosterRow[]) => {
        const r = await facilityRosterImport(client, { params: { orgId: target! }, body: { rows } });
        next = out.rows.map((x, i) => ({ ...x, id: r.results[i]?.id ?? null })).filter((_, i) => r.results[i]?.ok !== false);
        if (r.failed) setErr({ message: `${r.failed} not added: ${r.results.filter((x) => !x.ok).map((x) => `line ${x.row} (${x.error ?? 'refused'})`).join(', ')}` } as ApiError);
      };
      if (kind === 'Staff' && keyed && target) {
        await roster(out.rows.map((x) => ({ entity_type: 'staff' as const, name: x.a, email: x.b, role: x.role ?? SERVER_ROLE(x.c), ...(x.x ? { external_id: x.x } : {}), ...(x.p ? { phone: x.p } : {}) })));
      } else if (kind === 'Staff') {
        const r = await adminUsersBulk(client, { body: { users: out.rows.map((x) => ({ email: x.b, name: x.a, role: SERVER_ROLE(x.c), ...(target ? { organizationId: target } : {}), ...(x.p ? { phone: x.p } : {}) })) } });
        next = out.rows.map((x, i) => ({ ...x, id: r.results[i]?.id ?? null })).filter((_, i) => r.results[i]?.ok);
        const failed = r.results.filter((x) => !x.ok);
        if (failed.length) setErr({ message: `${failed.length} not added: ${failed.map((f) => `${f.email} (${f.error ?? 'refused'})`).join(', ')}` } as ApiError);
      } else {
        await roster(out.rows.map((x) => ({ entity_type: 'resident' as const, name: x.a, room: x.b, ...(x.x ? { external_id: x.x } : {}) })));
      }
      setAdded((p) => ({ ...p, [kind]: [...p[kind], ...next] }));
      setPaste('');
      setFileName('');
      onAdded();
    } catch (e) {
      setErr(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const pick = () => {
    if (typeof document === 'undefined') return;
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = '.csv,.tsv,.txt,.xls,.xlsx,text/csv';
    inp.onchange = () => {
      const f = inp.files && inp.files[0];
      if (!f) return;
      const rd = new FileReader();
      rd.onload = () => {
        const txt = String(rd.result || '');
        // an xlsx is a zip, so its bytes are not readable as text: say so rather than pasting noise
        if (/^PK\u0003\u0004/.test(txt)) {
          setFileErr(f.name + ' is an .xlsx workbook, which we cannot read directly yet. Save it as CSV, or open it and paste the rows.');
          setFileName('');
          return;
        }
        setPaste(txt);
        setFileName(f.name);
        setFileErr('');
      };
      rd.readAsText(f);
    };
    inp.click();
  };

  const saveEdit = async () => {
    if (!edit) return;
    const row = committed[edit.i];
    setErr(null);
    try {
      if (row.id && kind === 'Staff') await adminUserUpdate(client, { params: { id: row.id }, body: { name: edit.a, email: edit.b, role: SERVER_ROLE(edit.c) } });
      setAdded((p) => ({ ...p, [kind]: p[kind].map((x, j) => (j === edit.i ? { ...x, a: edit.a, b: edit.b, c: edit.c, flag: '' } : x)) }));
      setEdit(null);
      onAdded();
    } catch (e) {
      setErr(e as ApiError);
    }
  };

  const remove = (i: number) => {
    const row = committed[i];
    ask({
      sec: 'Users & Permissions',
      title: 'Remove ' + row.a + '?',
      body: row.id ? 'Their account is closed and they can no longer sign in. What they did stays in the record.' : 'They are taken off this list.',
      btn: 'Remove',
      go: async () => {
        if (row.id) await adminUserDelete(client, { params: { id: row.id } });
        setAdded((p) => ({ ...p, [kind]: p[kind].filter((_, j) => j !== i) }));
        onAdded();
      },
    });
  };

  return (
    <div data-testid="settings-bulk" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;margin-top:14px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:7px;')}>
        <span style={sx('font-size:14px;font-weight:700;letter-spacing:-.01em;')}>Add people in bulk</span>
        <span style={sx('flex:1 1 60px;min-width:30px;')} />
        {(['Staff', 'Residents'] as Kind[]).map((k) => (
          <a
            key={k}
            href="#"
            aria-pressed={kind === k}
            onClick={(e) => {
              pre(e);
              setKind(k);
              setPaste('');
              setFileName('');
            }}
            style={sx(chipK(kind === k))}
          >
            {I(k)}
          </a>
        ))}
      </div>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.55;margin-top:6px;max-width:88ch;text-wrap:pretty;')}>
        {I(kind === 'Staff' ? 'Name, email, mobile phone, role. Role can be blank and we will default it to Care Staff. Without a phone nobody can page them; each person confirms their own number with a code before any alert text or call goes to it.' : 'Name, room, consent. Consent can be blank and stays off until someone sets it.')}
      </div>
      {noBaa && (
        <div data-testid="bulk-baa-required" role="status" style={sx('font-size:16px;color:#8A5A00;background:rgba(138,90,0,.08);border-radius:8px;padding:8px 11px;line-height:1.5;margin-top:9px;max-width:88ch;text-wrap:pretty;')}>
          {I('Residents can be loaded once the BAA is signed by both sides and Aithora has recorded it. Staff can be added now.')}
        </div>
      )}
      {!fa && (
        <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:9px;')}>
          <span style={sx('font-size:16px;color:#3C454F;')}>{I('Add them to')}</span>
          <select
            aria-label="Building to add people to"
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
            style={sx('height:30px;max-width:100%;border-radius:7px;border:1px solid rgba(34,39,46,.2);background:#FBF8F1;font-size:16px;padding:0 8px;color:#22272E;')}
          >
            <option value="">{kind === 'Staff' ? 'Aithora (the team)' : 'Pick a building'}</option>
            {(buildings.data?.facilities ?? []).filter((f) => f.org_type === 'facility').map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>
      )}
      <textarea
        aria-label="People to add"
        value={paste}
        onChange={(e) => setPaste(e.target.value)}
        placeholder={kind === 'Staff' ? 'Maria Ortiz, m.ortiz@shadyacres.com, (512) 555-0100, Nurse\nJames Hall, j.hall@shadyacres.com, (512) 555-0101, CNA' : 'Beatrice Okafor, 214-A, family shared\nHarold Bishop, 108-B'}
        rows={4}
        style={sx('box-sizing:border-box;width:100%;padding:9px 11px;border:1px solid rgba(34,39,46,.18);border-radius:9px;background:#FBF8F1;font-variant-numeric:tabular-nums;font-size:16px;line-height:1.6;color:#22272E;resize:vertical;margin-top:10px;')}
      />
      <div style={sx('font-size:16px;color:#54606C;margin-top:6px;line-height:1.5;text-wrap:pretty;')}>{I('Paste straight from Excel or a CSV export. Tabs, commas and semicolons all work. A header row names the columns; a facility id column means loading the file again updates people instead of adding them twice.')}</div>
      {out.rows.length > 0 && (
        <div style={sx('margin-top:12px;border:1px solid rgba(47,138,111,.3);background:rgba(47,138,111,.06);border-radius:10px;padding:11px 13px;')}>
          <div style={sx('font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#1F6B52;font-weight:700;')}>{I(out.rows.length + ' ready to add')}</div>
          <div style={sx('display:grid;grid-template-columns:minmax(0,1.5fr) minmax(0,1.6fr) minmax(0,1.1fr);gap:8px 12px;margin-top:9px;padding-bottom:6px;border-bottom:1px solid rgba(34,39,46,.12);')}>
            <span style={sx(HEAD)}>{I('Name')}</span>
            <span style={sx(HEAD)}>{I(kind === 'Staff' ? 'Email' : 'Room')}</span>
            <span style={sx(HEAD)}>{I(kind === 'Staff' ? 'Role' : 'Consent')}</span>
          </div>
          {out.rows.map((p, i) => (
            <div key={i} style={sx('display:grid;grid-template-columns:minmax(0,1.5fr) minmax(0,1.6fr) minmax(0,1.1fr);gap:8px 12px;align-items:center;padding:6px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
              <span style={sx('font-size:16px;font-weight:600;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{I(p.a)}</span>
              <span style={sx('font-size:16px;color:#3C454F;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{I(p.b)}</span>
              <span style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
                <span style={sx('font-size:16px;color:#3C454F;')}>{I(p.c)}</span>
                {p.flag && <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.06em;color:#5B6B7C;background:rgba(91,107,124,.16);border-radius:4px;padding:2px 6px;white-space:nowrap;')}>{I(p.flag)}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
      {out.bad.length > 0 && (
        <div style={sx('margin-top:10px;border:1px solid rgba(179,38,30,.32);background:rgba(179,38,30,.06);border-radius:10px;padding:11px 13px;')}>
          <div style={sx('font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#B3261E;font-weight:700;')}>{I(out.bad.length + (out.bad.length === 1 ? ' row skipped' : ' rows skipped'))}</div>
          <div style={sx('font-size:16px;color:#54606C;line-height:1.5;margin-top:4px;text-wrap:pretty;')}>{I('These are listed rather than dropped quietly, so you can see exactly what did not come through.')}</div>
          {out.bad.map((b, i) => (
            <div key={i} style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:4px;padding:6px 0;border-top:1px solid rgba(179,38,30,.16);')}>
              <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;color:#B3261E;font-weight:700;flex:0 0 auto;')}>
                {'line '}
                {I(b.n)}
              </span>
              <span style={sx('font-size:16px;color:#3C454F;flex:1 1 130px;min-width:min(100%,130px);')}>{I(b.why)}</span>
              <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;color:#54606C;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{I(b.line)}</span>
            </div>
          ))}
        </div>
      )}
      <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:8px;margin-top:12px;')}>
        <a
          href="#"
          onClick={(e) => {
            pre(e);
            pick();
          }}
          style={sx(btn2(fileName ? 'soft' : 'go') + 'margin-right:8px;')}
        >
          {I(fileName ? 'Loaded ' + fileName : 'Choose a CSV file')}
        </a>
        <a
          href="#"
          role="button"
          aria-disabled={commitOff || !canWrite || undefined}
          title={roReason ?? undefined}
          onClick={(e) => {
            pre(e);
            void commit();
          }}
          style={sx(out.rows.length && !noBaa ? btn2('go') : btn2('off'))}
        >
          {I(noBaa ? 'Waiting for the BAA' : out.rows.length ? 'Add ' + out.rows.length + ' ' + kind.toLowerCase() : 'Nothing to add yet')}
        </a>
      </div>
      {fileErr && <div style={sx('font-size:16px;color:#B3261E;line-height:1.55;margin-top:8px;max-width:88ch;text-wrap:pretty;')}>{I(fileErr)}</div>}
      <ErrLine error={err} />
      <div style={sx('margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
        <div style={sx(HEAD)}>{I(committed.length + ' ' + kind.toLowerCase() + ' added')}</div>
        {committed.length === 0 && (
          <div style={sx('font-size:16px;color:#54606C;line-height:1.55;margin-top:6px;max-width:88ch;text-wrap:pretty;')}>
            {I(kind === 'Staff'
              ? 'No staff yet. Paste your list above, or invite people one at a time from Users and Permissions.'
              : 'No residents yet. Paste your list above. Consent stays off for everyone until you set it, so nothing starts talking to anybody by accident.')}
          </div>
        )}
        {committed.map((p, i) => (
          <div key={i} style={sx('padding:8px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
            {edit?.i !== i ? (
              <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:5px;')}>
                <span style={sx('font-size:16px;font-weight:600;flex:1 1 130px;min-width:min(100%,130px);')}>{I(p.a)}</span>
                <span style={sx('font-size:16px;color:#54606C;')}>{I(p.b)}</span>
                <span style={sx('font-size:16px;color:#54606C;')}>{I(p.c)}</span>
                <a href="#" onClick={(e) => { pre(e); setEdit({ i, a: p.a, b: p.b, c: p.c }); }} style={sx('font-size:16px;color:#2F5C97;text-decoration:none;white-space:nowrap;')}>edit</a>
                <a href="#" onClick={(e) => { pre(e); if (canWrite) remove(i); }} style={sx('font-size:16px;color:#B3261E;text-decoration:none;white-space:nowrap;')}>remove</a>
              </div>
            ) : (
              <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:7px;')}>
                <input type="text" aria-label="Name" value={edit.a} onChange={(e) => setEdit({ ...edit, a: e.target.value })} style={sx(EDIT_IN('flex:1 1 140px;min-width:min(100%,140px);'))} />
                <input type="text" aria-label={kind === 'Staff' ? 'Email' : 'Room'} value={edit.b} onChange={(e) => setEdit({ ...edit, b: e.target.value })} style={sx(EDIT_IN('flex:1 1 150px;min-width:min(100%,150px);'))} />
                <input type="text" aria-label={kind === 'Staff' ? 'Role' : 'Consent'} value={edit.c} onChange={(e) => setEdit({ ...edit, c: e.target.value })} style={sx(EDIT_IN('flex:1 1 110px;min-width:min(100%,110px);'))} />
                <a href="#" onClick={(e) => { pre(e); if (canWrite) void saveEdit(); }} style={sx(btn2('go') + 'height:28px;font-size:16px;')}>Save</a>
                <a href="#" onClick={(e) => { pre(e); setEdit(null); }} style={sx(btn2('soft') + 'height:28px;font-size:16px;')}>Cancel</a>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
